/**
 * Canonical ROAS pipeline for the three HROAS views (summary / over-time / revenue-streams).
 *
 * Decisions locked in HROAS/ModelResults/ROAS_CONSISTENCY_INVESTIGATION.md:
 *  - D1: Denominator universal across model toggle. Spend correction sees ALL available
 *    models; only the numerator (contribution) respects the toggle.
 *  - D2: Cutoff lives here as MIN_PERIOD_SPEND_FOR_ROAS so it is reconfigurable in one place.
 *  - D7: Date filter is part of the universal step-1 filter (applies before spend correction).
 *
 * Returns three things because numerator and denominator have different "universes":
 *  - contributionRows: toggle-filtered rows, one per (date, name, model_type, ...). Use these
 *    for the numerator. Each carries spend_corrected for convenience but DO NOT sum it
 *    directly across rows -- it would scale with N_enabled_models.
 *  - spendByDateName: toggle-INVARIANT lookup, max(spend) per (date, name). Use this for
 *    denominators by deduping per (date, name) at the view's grouping.
 *  - spendUniverse: deduped list with parsed channel parts, ready to be grouped by channel
 *    (or channel x period) for denominator iteration without re-parsing keys.
 *
 * Note: attribution_type === "actualized" is filtered upstream in
 * adaptSpendEffectShareRowsToActualizedCsvRows; ActualizedCsvRow has no attribution_type
 * field. We filter metric === "mean" here.
 */

import { ActualizedCsvRow } from './useModelResultsData';
import { parseChannelColumn } from './hroasUtils';

/** Minimum period spend (in $) below which a per-period ROAS is forced to 0 (View 2 only).
 *  Configurable per Econ's request. */
export const MIN_PERIOD_SPEND_FOR_ROAS = 50;

const METRIC_MEAN = 'mean';

const NAME_KEY_SEPARATOR = '|';

export interface ContributionRow extends ActualizedCsvRow {
  /** Aligned spend for this row's (date, name) -- equal across all model_types of the same channel. */
  spend_corrected: number;
  /** Composite key matching spendByDateName. Exposed for downstream dedup. */
  dateNameKey: string;
}

/** One entry per unique (date, name) in the universal-filtered set, with parsed channel
 *  parts and the aligned spend. Used by view code to iterate denominator components,
 *  grouped by channel / channel x period / etc., without re-parsing keys. */
export interface SpendUniverseEntry {
  dateNameKey: string;
  date: string;
  channel: string;
  platform: string;
  tactic: string;
  brand_nonbrand: string;
  spend_corrected: number;
}

export interface RoasPipelineOutput {
  /** Toggle-filtered rows. Numerator source. */
  contributionRows: ContributionRow[];
  /** Toggle-invariant aligned-spend lookup keyed by composite (date, channel, platform, tactic, brand_nonbrand). Denominator source. */
  spendByDateName: Map<string, number>;
  /** Toggle-invariant deduped (date, name) universe for denominator iteration. */
  spendUniverse: SpendUniverseEntry[];
}

export interface RoasPipelineOpts {
  enabledModelTypes: string[];
  /** Optional inclusive ISO date window. */
  dateRange?: { start: string; end: string };
}

export const buildDateNameKey = (
  date: string,
  channel: string | undefined | null,
  platform: string | undefined | null,
  tactic: string | undefined | null,
  brand_nonbrand: string | undefined | null
): string =>
  [date, channel ?? '', platform ?? '', tactic ?? '', brand_nonbrand ?? ''].join(
    NAME_KEY_SEPARATOR
  );

export const buildDateNameKeyFromRow = (row: ActualizedCsvRow): string => {
  const parsed = parseChannelColumn(row.channel, row.media_channel);
  const date = row.time || row.date || '';
  return buildDateNameKey(
    date,
    parsed.channel,
    parsed.platform,
    parsed.tactic,
    parsed.brand_nonbrand
  );
};

const inDateRange = (row: ActualizedCsvRow, range?: { start: string; end: string }): boolean => {
  if (!range) {
    return true;
  }
  const date = row.time || row.date || '';
  if (!date) {
    return false;
  }
  return date >= range.start && date <= range.end;
};

/**
 * Apply the canonical ROAS pipeline to actualized SES rows.
 *
 *  1. Universal filter: metric === "mean", non-null spend/effect, in dateRange.
 *  2. Spend correction over the full universe of model types: max per (date, name).
 *  3. Toggle filter: drop rows whose model_type isn't enabled. Survivors carry the
 *     toggle-invariant spend_corrected for convenience.
 */
export const applyRoasPipeline = (
  rows: ActualizedCsvRow[],
  opts: RoasPipelineOpts
): RoasPipelineOutput => {
  const universal = rows.filter(
    row =>
      row.metric === METRIC_MEAN &&
      row.actualized_spend != null &&
      row.actualized_effect != null &&
      inDateRange(row, opts.dateRange)
  );

  const spendByDateName = new Map<string, number>();
  const universeIndex = new Map<string, SpendUniverseEntry>();

  universal.forEach(row => {
    const key = buildDateNameKeyFromRow(row);
    const spend = Number(row.actualized_spend) || 0;
    const existingMax = spendByDateName.get(key);
    if (existingMax === undefined || spend > existingMax) {
      spendByDateName.set(key, spend);
    }
    if (!universeIndex.has(key)) {
      const parsed = parseChannelColumn(row.channel, row.media_channel);
      universeIndex.set(key, {
        dateNameKey: key,
        date: row.time || row.date || '',
        channel: parsed.channel || '',
        platform: parsed.platform || '',
        tactic: parsed.tactic || '',
        brand_nonbrand: parsed.brand_nonbrand || '',
        spend_corrected: 0
      });
    }
  });

  // Stamp each universe entry with the final aligned spend.
  const spendUniverse: SpendUniverseEntry[] = [];
  universeIndex.forEach((entry, key) => {
    spendUniverse.push({
      ...entry,
      spend_corrected: spendByDateName.get(key) ?? 0
    });
  });

  const enabled = new Set(opts.enabledModelTypes);
  const contributionRows: ContributionRow[] = [];
  universal.forEach(row => {
    if (!enabled.has(row.model_type)) {
      return;
    }
    const key = buildDateNameKeyFromRow(row);
    contributionRows.push({
      ...row,
      spend_corrected: spendByDateName.get(key) ?? 0,
      dateNameKey: key
    });
  });

  return { contributionRows, spendByDateName, spendUniverse };
};

/**
 * Sum aligned spend across a unique set of (date, name) keys. Used to compute denominators
 * at view-specific groupings without double-counting per-row aligned spend.
 */
export const sumDedupedSpend = (
  dateNameKeys: string[],
  spendByDateName: Map<string, number>
): number => {
  const seen = new Set<string>();
  let total = 0;
  dateNameKeys.forEach(key => {
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    total += spendByDateName.get(key) ?? 0;
  });
  return total;
};
